import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, Outlet, useNavigate } from 'react-router-dom';

import { useGetPersonsQuery } from '@client/api';
import {
  Button,
  Card,
  CardTitle,
  EmptyPlaceholder,
  PageTitle,
  Pagination,
  Table,
  TableBody,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  useRouteDocumentTitle,
} from '@client/toolkit';

import { UserListRow, UsersToolbar } from './components';
import { useUserListSearchQueryParams, useUserListSortQueryParams } from './utils/use-user-list-query-params';

import { UsersPaths } from '../users.router';
import { PageListLayout, PageContentAnimation } from '../../../components/layout';

export function UserListRoute() {
  const { t } = useTranslation('tenants');
  const { t: tCommon } = useTranslation();
  const { t: tGlobal } = useTranslation('global');

  useRouteDocumentTitle(t('documentTitle'), UsersPaths.Users);

  const navigate = useNavigate();

  const [sortQueryParams] = useUserListSortQueryParams();
  const [searchQueryParams] = useUserListSearchQueryParams();

  const pageSize = 100;
  const [page, setPage] = useState(0);

  const { data, isLoading, isFetching } = useGetPersonsQuery(
    {
      'pagination.Limit': pageSize,
      'pagination.Offset': page * pageSize,
      search: searchQueryParams.usersSearch ? searchQueryParams.usersSearch : undefined,
      'sort.Property': sortQueryParams.usersSortProperty ? sortQueryParams.usersSortProperty : undefined,
      'sort.Order': sortQueryParams.usersSortOrder ? sortQueryParams.usersSortOrder : undefined,
    },
    {
      refetchOnMountOrArgChange: true,
    },
  );

  const users = useMemo(() => data?.items ?? [], [data?.items]);
  const hasItems = Array.isArray(users) && users.length > 0;
  const totalCount = data?.totalCount ?? 0;

  const handleClickCreateUser = useCallback(() => navigate(generatePath(UsersPaths.UserCreate)), [navigate]);

  return (
    <PageListLayout breadcrumb={tGlobal('navigation.overview')}>
      <div className="flex items-start justify-between">
        <PageTitle className="pb-2" />
        <Button color="primary-dark" onClick={handleClickCreateUser}>
          {t('inviteUser')}
        </Button>
      </div>
      <PageContentAnimation>
        <Card className="mb-4 px-4" overflowX={false}>
          <UsersToolbar totalCount={totalCount} />
          <Card className="p-6 mb-16 bg-surface-base">
            <CardTitle variant="highlighted">{tGlobal('entity.user', { context: 'plural' })}</CardTitle>
            <Table className="border border-border rounded-md my-6">
              {!isLoading && !hasItems && (
                <caption className="px-4 py-4 [caption-side:bottom]">
                  <EmptyPlaceholder>
                    {tCommon('noEntityFound', { entity: tGlobal('entity.user', { context: 'plural' }) })}
                  </EmptyPlaceholder>
                </caption>
              )}

              <TableHead>
                <TableHeadRow>
                  <TableHeadCell>{tGlobal('user.name')}</TableHeadCell>
                  <TableHeadCell>{tGlobal('user.email')}</TableHeadCell>
                  <TableHeadCell>{tGlobal('user.country')}</TableHeadCell>
                  <TableHeadCell>{tGlobal('user.socialSecurityNumber')}</TableHeadCell>
                </TableHeadRow>
              </TableHead>
              <TableBody>
                {isLoading && Array.from(Array(10).keys()).map((x) => <UserListRow key={x} isFetching />)}

                {!isLoading &&
                  hasItems &&
                  users.map((user) => <UserListRow key={user.id} user={user} isFetching={isFetching} />)}
              </TableBody>
            </Table>
            <Pagination totalCount={totalCount} currentPage={page} setPage={setPage} pageSize={pageSize} />
          </Card>
        </Card>
      </PageContentAnimation>
      <Outlet />
    </PageListLayout>
  );
}
